import invariant from "ts-invariant";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  ButtonClickTriggerConfigs,
  isJsonEmbedMessage,
} from "@dots-projects/types";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import {
  saveDraft,
  updateButtonClickTriggerConfig,
} from "./reducer/actionCreators";
import { RootFormWithCommonParts } from "./RootFormWithCommonParts";
import { FormLabel } from "./FormLabel";
import { TriggerRootFormProps } from "./TriggerRootForm";
import EmojiPicker from "./EmojiPicker";
import { Input } from "./Input";
import TriggerEditorChannelSelector from "./TriggerEditorChannelSelector";
import TriggerEditorDiscordTextEditor from "./TriggerEditorDiscordTextEditor";
import { Checkbox } from "antd";
import EmbedMessageInput from "./EmbedMessageInput";

type TriggerRootFormButtonClickProps = TriggerRootFormProps;

const defaultEmbedMsg = (content: string) =>
  JSON.stringify({
    content: "",
    embeds: [
      {
        title: content,
      },
    ],
  });

export default function TriggerRootFormButtonClick({
  trigger,
}: TriggerRootFormButtonClickProps) {
  invariant(trigger.type === "button_click");
  const $$editorButtonClickContent = (
    trigger.configs as ButtonClickTriggerConfigs
  )?.$$editorButtonClickContent;

  const { buttonText, buttonEmoji, message, htmlMessage, channelId } =
    $$editorButtonClickContent || {};

  const defaultValues = {
    isEmbed: message ? isJsonEmbedMessage(message) : false,
    htmlMessage:
      htmlMessage ||
      "Welcome to the community! Click here to get access to the rest of the server.",
    message:
      message ||
      "Welcome to the community! Click here to get access to the rest of the server.",
    messageEmbed:
      message ||
      "Welcome to the community! Click here to get access to the rest of the server.",
    buttonText: buttonText || "Button text",
    buttonEmoji: buttonEmoji || "👋",
    channelId: channelId || "$channelId",
  };

  const form = useForm({
    defaultValues,
    mode: "onChange",
  });

  const { handleSubmit, control, register, setValue, watch } = form;
  const { state, dispatch, onPersist, validationErrors } =
    useTriggerEditorStateDispatch();
  const readOnly = !state.trigger.current.isDraft;

  const onSubmit = (data: any) => {
    // This uses the immer library to create a new copy of the trigger (with updates)
    // without affecting the original trigger
    const updateButtonClickTriggerConfiguration = {
      message: watch("isEmbed") ? data.messageEmbed : data.message,
      htmlMessage: watch("isEmbed") ? data.messageEmbed : data.htmlMessage,
      buttonText: data.buttonText,
      buttonEmoji: data.buttonEmoji,
      channelId: data.channelId,
    };

    dispatch(
      updateButtonClickTriggerConfig(updateButtonClickTriggerConfiguration)
    );
    dispatch(saveDraft(onPersist));
  };

  const [errorSelect, setErrorSelect] = useState(
    channelId ? ["Select channel", "$channelId"].includes(channelId) : true
  );

  const onChangeEmbed = (value: string) => {
    if (isJsonEmbedMessage(value)) {
      // on embed message input change we update "htmlMessage" and "message" fields as well, if possible we only place the "content" part of the embed on them.
      const objMsg = JSON.parse(value);
      setValue("htmlMessage", objMsg?.content ? objMsg?.content : value);
      setValue("message", objMsg?.content ? objMsg?.content : value);
    } else {
      setValue("htmlMessage", value);
      setValue("message", value);
    }
  };

  useEffect(() => {
    // validate input field programmatically when the input is switched, requiered to enable save button
    if (watch("isEmbed")) {
      // switch from normal text input to embed input
      if (isJsonEmbedMessage(watch("messageEmbed"))) {
        setValue("messageEmbed", watch("messageEmbed"), {
          shouldValidate: true,
        });
        return;
      }
    } else {
      // switch from embed to normal text input
      //we make sure to have a valid field in messageEmbed field even if it's not displayed.
      if (!isJsonEmbedMessage(watch("messageEmbed"))) {
        setValue("messageEmbed", defaultEmbedMsg(watch("message")), {
          shouldValidate: true,
        });
      }
    }
  }, [watch("isEmbed")]);

  return (
    <RootFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      form={form}
      trigger={trigger}
    >
      <FormLabel label="Message" />
      <Controller
        control={control}
        name="isEmbed"
        render={({ field }) => (
          <Checkbox
            disabled={readOnly}
            checked={field.value}
            onChange={field.onChange}
          >
            <span className="text-gray-500">Send as embed message</span>
          </Checkbox>
        )}
      />
      {watch("isEmbed") ? (
        <EmbedMessageInput
          form={form}
          onChangeEmbed={onChangeEmbed}
          name="messageEmbed"
        />
      ) : (
        <TriggerEditorDiscordTextEditor
          form={form}
          htmlField="htmlMessage"
          markdownField="message"
          defaultValue={defaultValues.message}
          readOnly={readOnly}
        />
      )}

      {/* ButtonEmoji */}
      <FormLabel label="Button text">
        <div
          className="flex w-full flex-row gap-3"
          onClick={(event) => event.preventDefault()}
        >
          <div className="basis-1/5">
            {" "}
            <Controller
              control={control}
              name="buttonEmoji"
              render={({ field }) => (
                <EmojiPicker
                  emoji={field.value}
                  onEmojiClick={field.onChange}
                />
              )}
            />
          </div>
          <Input
            className="basis-4/5"
            style={{ flexGrow: 1 }}
            readOnly={readOnly}
            {...register("buttonText")}
          />
        </div>
      </FormLabel>

      {/*Channel selector*/}
      <Controller
        control={control}
        name="channelId"
        render={({ field }) => (
          <TriggerEditorChannelSelector
            readOnly={readOnly}
            field={field}
            errorSelect={errorSelect}
            trigger={state.trigger.current}
            showRefreshButton
          />
        )}
      />
    </RootFormWithCommonParts>
  );
}
